import {
  type CoachSessionSnapshot,
  useCoachOptimizationStore,
} from "@/client/stores/coach-optimization";

import type { BuildCoachPlanResult } from "./build-coach-plan";
import type { ComponentTotals } from "./coach-attributed-score";

/**
 * Hand a set of AI suggestions to the BUILDER for review, instead of reviewing them on their own screen.
 *
 * WHY THIS EXISTS. Two entry points produce résumé suggestions and both should end up in the same
 * place:
 *
 *   - the LMS handoff (`/coach/optimize`) — a job, a baseline score, attributed per-suggestion gains
 *   - the dashboard (`/dashboard/ai-optimization`) — an uploaded job description, no job record
 *
 * The dashboard used to review them in a bespoke workspace embedded in its own page: a second résumé
 * preview, its own typography and layout panels, its own accept/reject list. That is the builder,
 * rebuilt at a fraction of its fidelity, and a student editing there could not see the rest of their
 * résumé while deciding. Both paths now seed the same store and open `/builder/:id`, so the suggestion
 * UI has exactly one implementation.
 *
 * ⚠️ THE RÉSUMÉ MUST ALREADY BE PERSISTED. The builder loads by id, so an uploaded file has to be
 * imported into a real résumé before this is called. That is the caller's job — it is also what decides
 * whether to import or to reuse a selected résumé.
 *
 * Docs: lms-backend/docs/plans/2026-09-23-coach-resume-improvement.md
 */
export const startCoachReviewInBuilder = ({
  session,
  resumeId,
  plan,
}: {
  session: CoachSessionSnapshot;
  /** Id of a résumé that already exists server-side — the builder loads by id. */
  resumeId: string;
  /** Output of `buildCoachPlan`, which has already verified and filtered everything in it. */
  plan: BuildCoachPlanResult;
}) => {
  useCoachOptimizationStore.getState().start({
    session,
    resumeId,
    suggestions: plan.suggestions,
    // Captured by buildCoachPlan BEFORE anything was accepted, which is what Reject restores from.
    originalValues: plan.originalValues,
    // Carried through even for a session with `scoreTracking: false`. The gains are the model's own
    // attribution and are what order the plan; the session flag is what decides whether any of it is
    // rendered as a number. Dropping them here instead would make the two entry points produce
    // different stores from the same AI output.
    gains: plan.gains,
    skillAdditions: plan.skillAdditions,
  });
};

/**
 * Table B's weights for the two components a résumé can move, renormalised to span 0-100.
 *
 * `roleDomainFit` is 25 and `skillOverlap` is 30 of a 100-point job-match score; the other 45 comes from
 * compensation, location, experience band and academics — none of which exist for an uploaded job
 * description. Renormalising over their sum (55) makes a résumé that fully satisfies the JD's role and
 * skill requirements read as 100 rather than capping at 55.
 *
 * ⚠️ This is a RÉSUMÉ-FIT score, not the LMS job-match score. Same donut, same tier cutoffs, same
 * colours — but it answers "how well does this résumé match this job description", which is the only
 * question that can be answered without a job record and a student profile. The two are not comparable
 * numbers, and nothing in this flow claims they are: there is no job card to carry it back to.
 */
export const RESUME_FIT_WEIGHTS = {
  roleDomainFit: (25 / 55) * 100,
  skillOverlap: (30 / 55) * 100,
} as const;

/**
 * Session for a dashboard optimization: no job record, no return trip — but a real score.
 *
 * The model's own assessment of the résumé becomes the baseline, so this flow shows the SAME score
 * donut, the same per-suggestion `+N` badges and the same projection as the LMS handoff. An earlier
 * version showed a progress bar instead, on the grounds that there was no authoritative baseline to
 * quote; that left the two entry points visibly different for what is meant to be one feature, and the
 * model's assessment is a real reading of the résumé against the JD rather than an invented number.
 *
 * ⚠️ `assessed` comes from the same call that produced the gains, which is what keeps the arithmetic
 * coherent: baseline and per-suggestion improvements are one model's opinion, so they add up. Mixing an
 * LMS baseline with a model's gains, or vice versa, is what made the projection unreliable before.
 *
 * `returnOrigin` is empty on purpose: there is no Career Coach tab to post back to, so the confirm
 * button reads "Save" and the unload guard stays off. Both follow from this one field.
 */
export const buildDashboardCoachSession = ({
  jobTitle,
  assessed,
}: {
  jobTitle: string;
  /** `BuildCoachPlanResult.assessed` — the model's read of the résumé as it stands. */
  assessed: ComponentTotals;
}): CoachSessionSnapshot => {
  const baselineScore = scoreFromComponents(assessed);

  return {
    jobUuid: "",
    jobTitle,
    companyName: "",
    returnOrigin: "",
    baselineScore,
    // Let the donut derive the tier from the score, rather than quoting a badge the LMS never sent.
    baselineBadge: null,
    baselineRoleDomainFit: assessed.roleDomainFit,
    baselineSkillOverlap: assessed.skillOverlap,
    // Nothing is fixed here — every point on offer is résumé-movable, which is exactly why the ceiling
    // is 100 for this flow where the LMS handoff's is often far lower.
    fixedContribution: 0,
    weights: RESUME_FIT_WEIGHTS,
    projectedCeiling: 100,
    missingSkills: [],
    presentSkills: [],
  };
};

/** Weighted total of the two editable components under RESUME_FIT_WEIGHTS. */
const scoreFromComponents = ({ roleDomainFit, skillOverlap }: ComponentTotals) =>
  Math.round(
    ((roleDomainFit * RESUME_FIT_WEIGHTS.roleDomainFit) / 100 +
      (skillOverlap * RESUME_FIT_WEIGHTS.skillOverlap) / 100) *
      100,
  ) / 100;
